import { readFileSync } from 'node:fs';
import {
  originalPositionFor,
  TraceMap,
  type SourceMapInput,
} from '@jridgewell/trace-mapping';
import type {
  ErrorPayload,
  ModuleGraph,
  Plugin,
  ResolvedConfig,
  ViteDevServer,
  WebSocketClient,
} from 'vite';

const packageName = 'runtime-error-plugin';

/**
 * Returns the HMR channel for listening to custom events from clients.
 *
 * Vite 5–7 expose `server.ws` (a WebSocketServer). Future versions may
 * move this to `server.environments.client.hot`. We try both so the
 * plugin keeps working if `server.ws` is eventually removed.
 */
function getHmrChannel(server: ViteDevServer) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = server as any;

  if (s.ws) {
    return s.ws;
  }

  // Vite Environment API fallback (experimental in Vite 6+)
  if (s.environments?.client?.hot) {
    return s.environments.client.hot;
  }

  return undefined;
}

/**
 * Returns the module graph used to resolve source-mapped stack traces.
 *
 * Similar to `getHmrChannel`, we fall back to the per-environment module
 * graph when `server.moduleGraph` is not available.
 */
function getModuleGraph(server: ViteDevServer): ModuleGraph | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = server as any;

  if (s.moduleGraph) {
    return s.moduleGraph;
  }

  if (s.environments?.client?.moduleGraph) {
    return s.environments.client.moduleGraph;
  }

  return undefined;
}

export default function viteRuntimeErrorOverlayPlugin(options?: {
  filter?: (error: Error) => boolean;
}): Plugin {
  let resolvedBase = '/';

  return {
    name: packageName,

    apply(config, env) {
      return env.command === 'serve' && !config.ssr;
    },

    configResolved(config: ResolvedConfig) {
      resolvedBase = config.base ?? '/';
    },

    transformIndexHtml() {
      // Use the resolved base path so the import works when Vite is
      // served under a non-root base (e.g. /my-app/@vite/client).
      const viteClientPath = `${resolvedBase}@vite/client`.replace(
        /\/\//g,
        '/',
      );

      return [
        {
          tag: 'script',
          attrs: { type: 'module' },
          children: clientScript(viteClientPath),
        },
      ];
    },

    configureServer(server) {
      const hmr = getHmrChannel(server);
      if (!hmr) {
        server.config.logger.warn(
          `[${packageName}] Could not find HMR channel (server.ws or server.environments.client.hot). Runtime error overlay is disabled.`,
        );

        return;
      }

      hmr.on(MESSAGE_TYPE, (data: unknown, client: WebSocketClient) => {
        const error = Object.assign(new Error(), data);
        if (!error.stack) {
          return;
        }

        if (options?.filter && !options.filter(error)) {
          return;
        }

        const moduleGraph = getModuleGraph(server);
        if (!moduleGraph) {
          return;
        }

        const { stack, loc } = rewriteStacktrace(
          error.stack,
          moduleGraph,
          resolvedBase,
        );

        const err: ErrorPayload['err'] = {
          name: error.name,
          message: error.message,
          stack,
          loc,
          plugin: packageName,
        };

        if (loc?.file) {
          err.id = loc?.file;
          const source = readFileSync(loc.file, 'utf-8');
          err.frame = generateCodeFrame(source, {
            line: loc.line,
            column: loc.column - 1,
          });
        }

        // Log structured error so the runtime error can be passed to the agentReadWorkflowOutputBuffer
        server.config.logger.error(
          `[RUNTIME_ERROR]${JSON.stringify({
            type: 'runtime-error',
            timestamp: Date.now(),
            name: error.name,
            message: error.message,
            stack,
            loc,
            frame: err.frame,
          })}`,
        );

        client.send({
          type: 'error',
          err,
        });

        // Notify the client that the error passed the filter so it can
        // inform the parent frame for analytics tracking.
        client.send(NOTIFY_TYPE, {});
      });
    },
  };
}

const MESSAGE_TYPE = `${packageName}:error`;
const NOTIFY_TYPE = `${packageName}:notify-parent`;

function clientScript(viteClientPath: string): string {
  return `
import { createHotContext } from "${viteClientPath}";
const hot = createHotContext("/__dummy__${packageName}");

function sendError(error) {
  if (!(error instanceof Error)) {
    error = new Error("(unknown runtime error)");
  }
  const serialized = {
    message: error.message,
    stack: error.stack,
  };
  hot.send("${MESSAGE_TYPE}", serialized);
}

// Only notify the parent frame after the server confirms the error passed
// the filter and the overlay will be shown.
hot.on("${NOTIFY_TYPE}", () => {
  try {
    window.parent.postMessage({ type: "runtime-error", id: null }, "*");
  } catch (_) {}
});

window.addEventListener("error", (evt) => {
  sendError(evt.error);
});

window.addEventListener("unhandledrejection", (evt) => {
  sendError(evt.reason);
});
`;
}

function cleanStack(stack: string) {
  return stack
    .split(/\n/g)
    .filter((l) => /^\s*at/.test(l))
    .join('\n');
}

function rewriteStacktrace(
  stack: string,
  moduleGraph: ModuleGraph,
  base: string,
): {
  stack: string;
  loc?: Pos & { file: string };
} {
  // When a non-root base is configured (e.g. "/my-app/"), browser stack
  // traces contain URLs like /my-app/src/App.tsx but the module graph
  // keys use the path without the base prefix (/src/App.tsx). We strip
  // the base prefix so lookups succeed.
  const basePrefix =
    base !== '/' && base.length > 1 ? base.replace(/\/$/, '') : '';

  let loc: (Pos & { file: string }) | undefined = undefined;
  const rewrittenStack = cleanStack(stack)
    .split('\n')
    .map((stackLine) => {
      return stackLine.replace(
        /^ {4}at (?:(\S+?) )?\(?(?:https|http):\/\/[^/]+(\/[^\s?]+).*:(\d+):(\d+)\)?$/,
        (input, varName, url, line, column) => {
          if (!url) {
            return input;
          }

          // Strip the base prefix for module graph lookups
          const lookupUrl =
            basePrefix && url.startsWith(basePrefix)
              ? url.slice(basePrefix.length)
              : url;

          const moduleEntry =
            moduleGraph.urlToModuleMap.get(lookupUrl) ||
            moduleGraph.urlToModuleMap.get(url);
          if (!moduleEntry) {
            return '';
          }

          const rawSourceMap = moduleEntry?.transformResult?.map;

          if (rawSourceMap) {
            const traced = new TraceMap(rawSourceMap as SourceMapInput);
            const pos = originalPositionFor(traced, {
              line: Number(line),
              // stacktrace's column is 1-indexed, but sourcemap's one is 0-indexed
              column: Number(column) - 1,
            });

            if (pos.source && pos.line >= 0 && pos.column >= 0) {
              line = pos.line;
              column = pos.column + 1;
            }
          }

          const trimmedVarName = varName?.trim();
          const sourceFile = moduleEntry.file;
          const source = `${moduleEntry.file}:${line}:${column}`;

          if (sourceFile) {
            loc ??= {
              line: Number(line),
              column: Number(column),
              file: sourceFile,
            };
          }

          if (!trimmedVarName || trimmedVarName === 'eval') {
            return `    at ${source}`;
          } else {
            return `    at ${trimmedVarName} ${source}`;
          }
        },
      );
    })
    .join('\n');

  return {
    stack: rewrittenStack,
    loc,
  };
}

type Pos = {
  /** 1-based */
  line: number;
  /** 0-based */
  column: number;
};

const splitRE = /\r?\n/g;
const range: number = 2;

function posToNumber(source: string, pos: number | Pos): number {
  if (typeof pos === 'number') {
    return pos;
  }

  const lines = source.split(splitRE);
  const { line, column } = pos;
  let start = 0;
  for (let i = 0; i < line - 1 && i < lines.length; i++) {
    start += lines[i].length + 1;
  }

  return start + column;
}

function generateCodeFrame(
  source: string,
  start: number | Pos = 0,
  end?: number | Pos,
): string {
  start = Math.max(posToNumber(source, start), 0);
  end = Math.min(
    end !== undefined ? posToNumber(source, end) : start,
    source.length,
  );
  const lines = source.split(splitRE);
  let count = 0;
  const res: Array<string> = [];
  for (let i = 0; i < lines.length; i++) {
    count += lines[i].length;
    if (count >= start) {
      for (let j = i - range; j <= i + range || end > count; j++) {
        if (j < 0 || j >= lines.length) {
          continue;
        }

        const line = j + 1;
        res.push(
          `${line}${' '.repeat(Math.max(3 - String(line).length, 0))}|  ${
            lines[j]
          }`,
        );
        const lineLength = lines[j].length;
        if (j === i) {
          const pad = Math.max(start - (count - lineLength), 0);
          const length = Math.max(
            1,
            end > count ? lineLength - pad : end - start,
          );
          res.push(`   |  ` + ' '.repeat(pad) + '^'.repeat(length));
        } else if (j > i) {
          if (end > count) {
            const length = Math.max(Math.min(end - count, lineLength), 1);
            res.push(`   |  ` + '^'.repeat(length));
          }

          count += lineLength + 1;
        }
      }

      break;
    }

    count++;
  }

  return res.join('\n');
}
